import React from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { couleurAnneau } from './ScoreRing';
import { t } from '../i18n';
import { colors, formes, radius, shadow, spacing, typography } from '../theme/tokens';
import type { CvCriteria } from '../types';

const CLES: (keyof CvCriteria)[] = ['keywords', 'layout', 'impact', 'readability'];

export interface CriteresScoreProps {
  criteres: CvCriteria;
  testID?: string;
}

// Une note à afficher : entière et bornée, quoi que le serveur ait renvoyé.
const borne = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0);

/**
 * « Détail par critère » : les quatre notes qui composent le score ATS, chacune en barre.
 *
 * Les valeurs sont celles du serveur, jamais recalculées ici ; la couleur de la barre suit la règle
 * de l'anneau (`couleurAnneau`) pour que les deux disent la même chose. Aucun gain en points : le
 * serveur n'en mesure pas.
 */
export function CriteresScore({ criteres, testID = 'criteres' }: CriteresScoreProps) {
  // Une valeur absente ou illisible n'est pas un 0 : sans les quatre notes, on n'affiche rien.
  if (!CLES.every((cle) => Number.isFinite(criteres?.[cle]))) return null;
  return (
    <View testID={testID} style={styles.bloc}>
      <Text accessibilityRole="header" style={styles.titre}>
        {t('cvScore.criteres.titre')}
      </Text>
      <View style={styles.carte}>
        {CLES.map((cle) => {
          const note = borne(criteres[cle]);
          const libelle = t(`cvScore.criteres.${cle}`);
          return (
            <View
              key={cle}
              testID={`${testID}-${cle}`}
              accessible
              accessibilityLabel={`${libelle}, ${note} ${t('common.scoreSur100')}`}
              style={styles.ligne}
            >
              <Text style={styles.libelle} numberOfLines={2}>
                {libelle}
              </Text>
              <View accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={styles.piste}>
                <View
                  testID={`${testID}-${cle}-barre`}
                  style={[styles.remplissage, { width: `${note}%`, backgroundColor: couleurAnneau(note) }]}
                />
              </View>
              <Text testID={`${testID}-${cle}-note`} style={styles.note}>
                {note}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bloc: { gap: spacing.sm },
  titre: {
    fontSize: typography.caption.size + 1,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing + 0.2,
    textTransform: 'uppercase',
    color: colors.grey500,
  },
  carte: {
    gap: spacing.md,
    padding: spacing.lg,
    borderRadius: formes.rayon.lg,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  // Largeur partagée par les quatre libellés : les barres démarrent toutes au même endroit.
  libelle: { width: '36%', fontSize: typography.small.size, lineHeight: typography.small.lineHeight, fontWeight: '700', color: colors.night },
  piste: { flex: 1, height: 8, borderRadius: radius.pill, overflow: 'hidden', backgroundColor: colors.grey100 },
  remplissage: { height: '100%', borderRadius: radius.pill },
  note: { minWidth: 28, textAlign: 'center', fontSize: typography.small.size, fontWeight: '800', color: colors.night },
});
